import { proStudioUrqlGql as urqlGql, Type } from '@pypestream/api-services';
import { TwilioConfigInput } from '@pypestream/api-services/urql';
import { throwErrorWithId } from '@pypestream/utils';

import { Channel } from './get-channels-from-pypes';
import { checkForErrors } from './check-for-errors';

/**
 * Re-exported, not declared: the name is one value with two readers — the
 * catalog rule in `platform-catalog` and this channel-creation path — and
 * `platform-catalog` is the lighter module of the two (this one imports
 * `api-services`), so the declaration lives there.
 */
export { CONTACT_CENTER_INTEGRATION_NAME } from './platform-catalog';

export type ChannelMutationProps = Omit<
  Channel,
  | 'id'
  | 'parent'
  | 'lastEdited'
  | 'name'
  | 'customPypeCss'
  | 'logo'
  | 'config'
  | 'agentRouting'
  | 'botRetry'
> & {
  id?: string;
  name?: string;
  customPypeCss?: string;
  voiceAiConfig?: TwilioConfigInput;
};

export const mutateChannel = async ({
  channel,
  projectId,
  requestAction,
  cb,
  uniqueCustomerId,
  botId,
}: {
  channel: ChannelMutationProps;
  projectId: string;
  requestAction: string;
  cb?: () => void;
  uniqueCustomerId?: string;
  botId?: string;
}): Promise<{ id?: string } | void> => {
  // Handle voice channels differently
  if (channel.type === 'twilio_voice') {
    return mutateVoiceChannel({
      channel,
      projectId,
      cb,
      uniqueCustomerId,
      botId,
    });
  }

  // Handle widget channels (existing logic)
  const {
    env,
    pypeId,
    id: widgetId = '',
    streamId,
    style,
    beta,
    name = '',
    customPypeCss = '',
  } = channel;

  const action = requestAction === 'update' ? 'put' : 'post';

  const mutation = {
    post: {
      live: urqlGql.mutatePostWidgetLive,
      sandbox: urqlGql.mutatePostWidgetSandbox,
    },
    put: {
      live: urqlGql.mutatePutWidgetLive,
      sandbox: urqlGql.mutatePutWidgetSandbox,
    },
  };

  const mutateWidget = mutation[action][env];

  // No agent-routing write here. Binding a channel to a live agent platform is a
  // `PypeConnection` membership change (`assignPypeConnection`), and the BACKEND
  // propagates `agent_routing` / `agent_routing_config` / `agent_routing_url`
  // onto the bound pypes. This used to PUT the channel's pype directly, which
  // meant the channel held a *copy* of the connector's config and the binding
  // could only be recovered by matching values. See backend#1262.
  const res = await mutateWidget({
    pypeId,
    projectId,
    ...(requestAction !== 'new' && { widgetId }),
    streamId,
    requestAction,
    style: JSON.stringify(style),
    beta: JSON.stringify(beta),
    widget_name: name,
    custom_pype_css: customPypeCss,
  });

  checkForErrors(res);

  cb?.();

  // Return channel ID if available from the mutation response
  const channelId = widgetId;
  return channelId ? { id: channelId } : undefined;
};

const mutateVoiceChannel = async ({
  channel,
  projectId,
  cb,
  uniqueCustomerId,
  botId,
}: {
  channel: ChannelMutationProps;
  projectId: string;
  cb?: () => void;
  uniqueCustomerId?: string;
  botId?: string;
}) => {
  const { env, streamId, voiceAiConfig, name } = channel;

  if (!voiceAiConfig) {
    throwErrorWithId('Voice AI configuration is required for voice channels');
    return;
  }

  const mutation = {
    live: urqlGql.patchV3ConfigurationCustomersCustomerIdChannelChannelId,
    sandbox:
      urqlGql.patchV3ConfigurationCustomersCustomerIdChannelChannelIdSandbox,
  };

  const mutateVoiceChannelMutation = mutation[env];

  if (!uniqueCustomerId || !botId) {
    throwErrorWithId(
      'uniqueCustomerId and botId are required for voice channel updates'
    );
    return;
  }

  const resp = await mutateVoiceChannelMutation({
    channelId: streamId,
    projectId,
    customerId: uniqueCustomerId,
    v3ConfigurationCustomerChannelInput: {
      config: {
        twilioConfig: voiceAiConfig,
      },
      type: Type.TwilioVoice,
      startBot: botId,
      name,
    },
  });

  checkForErrors(resp);

  cb?.();
};
